/**
 * The render system. Always LAST in the tick order (ARCHITECTURE.md §3.1); the actual draw
 * happens in `lateUpdate` so every other system has finished writing presentation state.
 *
 * Reads only interpolated state. Writes no gameplay state. If this file ever needs to touch
 * `ctx.sim`, that is a design error — see ARCHITECTURE.md §1.2.
 */

import * as THREE from 'three';
import { System, DisposalBag, clamp } from '../core/index.js';
import { createRenderer, applyQuality, applyExposureBias, resize, setRenderScale,
  readRendererInfo } from './renderer.js';
import { LightingRig } from './lighting.js';
import { CameraDirector } from './camera-director.js';

/** Dynamic resolution reacts over this many frames, so it never oscillates visibly. */
const DRS_WINDOW = 45;

export class RenderSystem extends System {
  static id = 'render';
  static deps = ['combat-sim'];
  static budgetMs = 1.2;

  constructor() {
    super();
    this.bag = new DisposalBag('render');
    this.renderer = null;
    this.scene = null;
    this.lighting = new LightingRig();
    this.camera = new CameraDirector();
    this._drsAccum = 0;
    this._drsFrames = 0;
    this._drsSlowFrames = 0;
    this._drsSevereFrames = 0;
    this._drsCleanWindows = 0;
    this._unsubs = [];
    /** Set true by tools so dynamic resolution and other adaptive behaviour cannot alter a capture. */
    this.pinned = false;
    this.shaderCompilesDuringCombat = 0;
    this._inCombat = false;
  }

  async init(ctx) {
    const canvas = ctx.render.canvas;
    if (!canvas) throw new Error('render: ctx.render.canvas is not set. main.js must supply it.');

    this.renderer = createRenderer(canvas, ctx);
    this.bag.custom(() => this.renderer.dispose());

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x0a0d11);
    // Distance fog: separates the fighters from the background and hides the placeholder
    // stage's abrupt edges. Tuned to start beyond the fighters so it never veils them.
    this.scene.fog = new THREE.Fog(0x0a0d11, 14, 58);
    ctx.render.scene = this.scene;

    this.scene.add(this.lighting.init(ctx, this.bag));
    this.camera.init(ctx);

    applyQuality(this.renderer, ctx, ctx.config.get('preset'));
    applyExposureBias(this.renderer, ctx);
    resize(this.renderer, ctx, ctx.render.width, ctx.render.height, ctx.render.dpr);

    this._unsubs.push(ctx.config.onChange('preset', (name) => {
      applyQuality(this.renderer, ctx, name);
      this.lighting.applyQuality(ctx.config.quality);
      const p = ctx.events.emit('engine:quality', {
        tick: ctx.time.tick, preset: name, reason: 'user', renderScale: ctx.render.renderScale,
      });
      void p;
    }));
    this._unsubs.push(ctx.config.onChange('exposureBias', () => applyExposureBias(this.renderer, ctx)));

    // Track whether we are in active combat, so shader compilation during a round can be
    // reported as the defect ARCHITECTURE.md §11.3 says it is.
    this._unsubs.push(ctx.events.on('combat:round-start', () => {
      this._inCombat = true;
      this.shaderCompilesDuringCombat = 0;
    }));
    this._unsubs.push(ctx.events.on('combat:round-end', () => { this._inCombat = false; }));

    // NOTE: prewarm is deliberately NOT called here. Systems that add geometry to the scene
    // (dev-overlay, and later fx/world/fighters) initialise AFTER render, so compiling at this
    // point would miss their materials and they would stall on first use. `main.js` calls
    // `prewarm()` once every system has initialised. See ARCHITECTURE.md §11.3.
  }

  /**
   * Compiles every material variant before gameplay begins (ARCHITECTURE.md §11.3).
   *
   * Temporarily forces the whole scene visible: `compile` skips invisible objects, and the
   * collision overlay is hidden by default — which is exactly how a shader ended up compiling
   * mid-combat the first time the overlay was switched on.
   */
  async prewarm(ctx) {
    if (!this.renderer || !this.scene || !this.camera.camera) return 0;
    const before = this.renderer.info.programs ? this.renderer.info.programs.length : 0;

    const hidden = [];
    const culled = [];
    this.scene.traverse((node) => {
      if (node.visible === false) {
        hidden.push(node);
        node.visible = true;
      }
      // Shadow/depth variants can otherwise be skipped when a caster happens to begin outside
      // the camera or shadow frustum, then compile the first time an animation moves it inside.
      if (node.frustumCulled === true && (node.isMesh || node.isLine || node.isPoints)) {
        culled.push(node);
        node.frustumCulled = false;
      }
    });

    try {
      if (typeof this.renderer.compileAsync === 'function') {
        await this.renderer.compileAsync(this.scene, this.camera.camera);
      } else {
        this.renderer.compile(this.scene, this.camera.camera);
      }
      // `compile` walks the scene with traverseVisible and does not reliably produce every
      // program a real draw will need. Rendering one actual frame with everything visible is the
      // ground truth: whatever the renderer needs, it creates now. ARCHITECTURE.md §11.3 asks for
      // exactly this — one off-screen frame containing every material variant at its final
      // defines — and it is the only thing that reliably caught the collision overlay's
      // MeshBasicMaterial, which otherwise compiled the first time hitboxes were switched on.
      this.renderer.render(this.scene, this.camera.camera);
    } finally {
      for (const node of hidden) node.visible = false;
      for (const node of culled) node.frustumCulled = true;
    }

    const after = this.renderer.info.programs ? this.renderer.info.programs.length : 0;
    ctx.log.info(
      `render: prewarmed ${after} shader programs (was ${before}, ` +
        `${hidden.length} hidden object(s) force-compiled)`,
    );
    this._programsAfterPrewarm = after;
    return after;
  }

  update(dt, ctx) {
    this.camera.update(dt, ctx);
    this.lighting.update(dt, ctx);

  }

  lateUpdate(dt, ctx) {
    if (!this.renderer || !this.scene || !this.camera.camera) return;

    this.renderer.info.reset();
    this.renderer.render(this.scene, this.camera.camera);

    const info = readRendererInfo(this.renderer);
    ctx.metrics.setCounter('render.drawCalls', info.drawCalls);
    ctx.metrics.setCounter('render.triangles', info.triangles);
    ctx.metrics.setCounter('render.programs', info.programs);

    // A program appearing after prewarm is a stall the player will feel.
    if (this._programsAfterPrewarm !== undefined && info.programs > this._programsAfterPrewarm) {
      const extra = info.programs - this._programsAfterPrewarm;
      this._programsAfterPrewarm = info.programs;
      ctx.metrics.counter('render.lateShaderCompiles', extra);
      if (this._inCombat) {
        this.shaderCompilesDuringCombat += extra;
        ctx.log.warnOnce('late-shader',
          `render: ${extra} shader program(s) compiled DURING combat — prewarm is incomplete`);
      }
    }

    if (!this.pinned && ctx.config.get('dynamicResolution')) this._dynamicResolution(dt, ctx);
  }

  /**
   * Dynamic resolution. Moves only within [preset * 0.7, preset] and never touches a defining
   * visual feature (ARCHITECTURE.md §9, §16) — so a benchmark cannot be flattered by silently
   * disabling rain or reflections.
   */
  _dynamicResolution(dt, ctx) {
    const frameMs = ctx.metrics.frameChannel.last;
    this._drsAccum += frameMs;
    if (frameMs > 20) this._drsSlowFrames++;
    if (frameMs > 25) this._drsSevereFrames++;
    this._drsFrames++;
    if (this._drsFrames < DRS_WINDOW) return;
    const avg = this._drsAccum / this._drsFrames;
    const slowFrames = this._drsSlowFrames;
    const severeFrames = this._drsSevereFrames;
    this._drsAccum = 0;
    this._drsFrames = 0;
    this._drsSlowFrames = 0;
    this._drsSevereFrames = 0;

    const preset = ctx.config.quality;
    const current = ctx.render.renderScale;
    let target = current;
    // Three 20 ms frames in this window are already enough to miss the slower-p95 target even
    // when many very fast frames keep the mean healthy. React to that tail directly instead of
    // allowing an unstable 10/25/10/25 ms cadence to hide behind a good average.
    if (avg > 19.5 || slowFrames >= 3 || severeFrames > 0) {
      target = current - 0.05;
      this._drsCleanWindows = 0;
    } else if (avg < 13.5 && slowFrames === 0) {
      this._drsCleanWindows++;
      // Recover cautiously: one clean window after a reduction is not enough evidence that the
      // expensive cadence is gone. This avoids alternating between adjacent scales every 750 ms.
      if (this._drsCleanWindows >= 4) {
        target = current + 0.04;
        this._drsCleanWindows = 0;
      }
    } else {
      this._drsCleanWindows = 0;
    }
    target = clamp(target, preset.renderScale * 0.7, preset.renderScale);
    if (Math.abs(target - current) > 0.004) {
      // The 45-frame observation window already supplies the temporal damping. Applying the
      // complete five-percent step here lets the next window measure whether that decision helped;
      // damping the step a second time made the scaler move by barely one percent per window.
      setRenderScale(this.renderer, ctx, target);
    }
    void dt;
  }

  resize(width, height, ctx) {
    if (!this.renderer) return;
    resize(this.renderer, ctx, width, height, ctx.render.dpr);
  }

  /**
   * Clears every piece of presentation state that carries across frames, then re-poses from the
   * current simulation state. This is the reproducibility contract for captures.
   *
   * ANY visual system with smoothed, decayed or accumulated state must reset it here. Three
   * separate capture-determinism bugs came from exactly this: decaying impact lights, camera
   * smoothing residual, and asymptotically-decayed limb rotations. Each was invisible frame to
   * frame and each made the same scenario render differently depending on what preceded it.
   *
   * @param {number} storm 0..1 weather ramp to apply directly, or -1 to keep the match value
   */
  resetVisuals(ctx, storm = -1) {
    this.lighting.setStorm(storm >= 0 ? storm : (ctx.sim.match ? ctx.sim.match.storm : 0));
    this.lighting.reset();
    const fighters = ctx.systems.get('fighters-visual');
    if (fighters && fighters.resetVisuals) fighters.resetVisuals(ctx);
    const world = ctx.systems.get('world');
    if (world && world.resetVisuals) world.resetVisuals(ctx);
    const fx = ctx.systems.get('fx');
    if (fx && fx.resetVisuals) fx.resetVisuals(ctx);
    this.camera.snapTo(ctx);
    this.setPinned(true, ctx);
  }

  /** Pins adaptive behaviour for reproducible captures. */
  setPinned(on, ctx) {
    this.pinned = on;
    this._drsAccum = 0;
    this._drsFrames = 0;
    this._drsSlowFrames = 0;
    this._drsSevereFrames = 0;
    this._drsCleanWindows = 0;
    if (on) setRenderScale(this.renderer, ctx, ctx.config.quality.renderScale);
  }

  rendererInfo() {
    return this.renderer ? readRendererInfo(this.renderer) : null;
  }

  dispose() {
    for (const off of this._unsubs) off();
    this._unsubs.length = 0;
    this.camera.dispose();
    if (this.scene) {
      this.scene.clear();
    }
    this.bag.disposeAll();
    this.renderer = null;
    this.scene = null;
  }
}
